/**
 * SRC-20 contract-mirroring constants — see spec §7.1 and §16.
 *
 * Reader ABI mirrors the deployed contract EXACTLY: `read(address,address)`
 * returning `uint256`. There is no EIP-712 typed-data parameter, no `relayer`,
 * no `expiry`, no `signature`. If you find yourself adding parameters here
 * you are wrong — re-read spec §0/§16 before changing.
 */
import type { Hex } from 'viem'
import type { Src20ReaderError } from './types'

/**
 * The deployed contract declares `read(...)` as `external returns (uint256)` (no
 * `view`). We mirror it locally as `view` for two practical reasons:
 *   1. seismic-viem's `sreadContract` typing restricts to `'pure' | 'view'`;
 *      this lets us use the higher-level signed-read helper directly.
 *   2. Functionally a signed read does not mutate state — Seismic's
 *      `signed_read: true` envelope runs the call like `eth_call`.
 * ABI mutability is purely client-side metadata; the network ignores it.
 */
export const READER_ABI = [
  {
    type: 'function',
    name: 'read',
    stateMutability: 'view',
    inputs: [
      { name: 'safe', type: 'address' },
      { name: 'src20', type: 'address' },
    ],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * SRC-20 shielded transfer selector. NOT the standard ERC-20 `0xa9059cbb`.
 * Solidity signature: `transfer(address,suint256)` where `suint256` is
 * Seismic's shielded uint256 type. Calldata layout: selector || abi.encode(recipient, amount).
 */
export const SRC20_TRANSFER_SELECTOR: Hex = '0xb10c99b5'

/** For reference / off-chain decoding only — keccak256("balance()")[:4]. */
export const SRC20_BALANCE_SELECTOR: Hex = '0xb69ef8a8'

/**
 * Mapping from the deployed reader's revert tails to internal errors.
 *
 * Keys are the substring AFTER the `SafeSrcBalanceReaderOwner...:` contract-name
 * prefix. The runtime tolerates suffixes on the contract name (e.g. `V2`) so a
 * future redeploy doesn't silently break error mapping.
 */
export const READER_REVERT_TAILS_TO_ERROR: Record<string, () => Src20ReaderError> = {
  'caller is not a safe owner': () => ({ kind: 'notOwner' }),
  'module call failed': () => ({ kind: 'moduleCallFailed' }),
  'unexpected return length': () => ({ kind: 'badTokenReturn' }),
}

/** Regex used to detect a reader-emitted revert reason in arbitrary error text. */
export const READER_REVERT_REGEX = /SafeSrcBalanceReaderOwner[A-Za-z0-9_]*:\s*([^"\n]+)/

/** Balance-value cache TTL (5 min) — UX optimization, never a signature. */
export const BALANCE_VALUE_CACHE_TTL_MS = 5 * 60 * 1000

/** localStorage key prefix for the balance-value cache. */
export const SRC20_BALANCE_CACHE_KEY_PREFIX = 'safeWallet:src20:balance'

/** Explorer auto-discovery in-memory cache TTL (60s per spec §9). */
export const EXPLORER_LIST_CACHE_TTL_MS = 60_000
